import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, ClickAwayListener, Grow, Typography } from '@mui/material';
import MuiPopper from '@mui/material/Popper';
import { ThemeProvider, createTheme, styled as muiStyled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { displayRequestRef } from '../../../../utils.js';
import { backYouTheme } from '../../../../theme.ts';
import { useMemo } from 'react';
import { IconButton } from '@mui/material';
import styled from 'styled-components';

export default function EventPopover({ event, anchorEl, onClose }) {
    const { t: gctrad } = useTranslation();

    const subEvents = event?.extendedProps?.groupedEvents ?? [];
    const [open, setOpen] = useState(false);
    const [anchorEvtEl, setAnchorEvtEl] = useState(null);
    const [arrowRef, setArrowRef] = useState(null);

    useEffect(() => {
        setOpen(!!anchorEl);
        setAnchorEvtEl(anchorEl)
    }, [anchorEl]);

    useEffect(() => {
        if (!open) {
            setAnchorEvtEl(null);
            onClose();
        }
    }, [open]);

    const PopperTheme = useMemo(() =>
        createTheme({
            palette: {
                primary: backYouTheme.palette.primary,
                secondary: backYouTheme.palette.secondary
            },
            typography: backYouTheme.typography
        }),
        [backYouTheme]);

    let lignesMeetingRooms = [];
    let lignesProduits = new Map();
    if (subEvents.length) {
        subEvents.map((subEvent) => {
            subEvent?.extendedProps?.lignes.map((ligne) => {
                if (ligne.meeting_rooms_type_slug) {
                    if (lignesMeetingRooms.length < 1) lignesMeetingRooms.push(ligne);
                }
                else if (event.extendedProps.status !== 'lce_bloque' || lignesProduits.size < 1) {
                    const idx = (ligne.time_html + ligne.title).hashCode();
                    lignesProduits.set(idx, ligne);
                }
            })
        })
    }

    const right_hotelier_gestion_demandes = !!AUTH.membres_rights.includes('hotelier_gestion_demandes');

    return (
        event && (
            <ThemeProvider theme={PopperTheme}>
                <Popper
                    open={open}
                    anchorEl={anchorEl}
                    transition
                    placement="top"
                    disablePortal
                    modifiers={[{
                        name: 'arrow',
                        enabled: true,
                        options: {
                            element: arrowRef
                        }
                    }]}
                >
                    {({ TransitionProps }) => (
                        <Grow
                            {...TransitionProps}
                            style={{
                                marginTop: 3,
                                marginBottom: 3
                            }}
                        >
                            <div>
                                <Arrow ref={setArrowRef} className="MuiPopper-arrow" />
                                <ClickAwayListener
                                    onClickAway={() => anchorEvtEl === anchorEl ? setOpen(false) : false}
                                >
                                    <Paper
                                        sx={{ minWidth: 280, maxWidth: 420, overflow: 'auto' }}
                                        elevation={4}
                                    >
                                        <Box sx={{ fontWeight: backYouTheme.typography.fontWeightRegular, backgroundColor: backYouTheme.palette.primary.darkBlueLight }}>
                                            <Box
                                                sx={{ padding: '.5rem' }}
                                                color="white"
                                            >
                                                <div className='d-flex justify-content-between align-items-end mr-4'>
                                                    <strong style={{ fontSize: '1.1em', lineHeight: '1.1' }}>
                                                        {capitalize(event.title)}
                                                    </strong>
                                                    <Badge>
                                                        <span className="badge badge-status d-flex align-items-center">
                                                            <i className="fa-solid fa-circle" style={{ color: event.extendedProps.status_color }}></i>
                                                            <span className='text-dark'>{event.extendedProps.status_name}</span>
                                                        </span>
                                                    </Badge>
                                                </div>
                                                <IconButton
                                                    aria-label="close"
                                                    disableFocusRipple={true}
                                                    disableRipple={true}
                                                    onClick={() => setOpen(false)}
                                                    sx={{
                                                        fontSize: '.8em',
                                                        position: 'absolute',
                                                        right: 3,
                                                        top: 3,
                                                        color: "white"
                                                    }}
                                                >
                                                    <CloseIcon sx={{ fontSize: '.8em' }} />
                                                </IconButton>
                                                {event.extendedProps.meeting_room == 1 && (
                                                    lignesMeetingRooms.map((ligne, i) => (
                                                        <div key={i}>
                                                            {ligne.pax > 0 && (
                                                                <span style={{ fontSize: '.8em' }}>
                                                                    <i className="fa-regular fa-user mr-1"></i>
                                                                    <span className='mr-1'>x{ligne.pax}</span>
                                                                </span>
                                                            )}
                                                            {ligne.meeting_rooms_type_title && (
                                                                <span style={{ fontSize: '.8em' }}>
                                                                    {ligne.meeting_rooms_type_title}
                                                                </span>
                                                            )}
                                                        </div>
                                                    ))
                                                )}
                                                <div style={{ fontSize: '.7em' }}>
                                                    {event.extendedProps.date_html}
                                                </div>
                                                <div className='d-flex justify-content-between' style={{ fontSize: '.7em' }}>
                                                    <span>{event.extendedProps.time_start}</span>
                                                    <span>{event.extendedProps.time_end}</span>
                                                </div>
                                            </Box>
                                        </Box>
                                        <CardContent className={event.extendedProps.conflit == 1 ? 'conflit' : ''}>
                                            {[...lignesMeetingRooms, ...lignesProduits.values()].map((ligne, i) => (
                                                <Typography color="text.secondary" key={i} sx={{ fontSize: '.7em' }}>
                                                    {event.extendedProps.conflit == 1 && (
                                                        <i className='fa-regular fa-triangle-exclamation mr-1 text-danger'></i>
                                                    )}
                                                    {ligne.time_html} : {!ligne.meeting_rooms_type_slug && ligne.qty > 0 ? `${ligne.qty} x ` : ''} {ligne.title}
                                                </Typography>
                                            ))}
                                        </CardContent>
                                        {(
                                            event.extendedProps.devisLink ||
                                            event.extendedProps.functionSheetLink ||
                                            event.extendedProps.editLink ||
                                            event.extendedProps.moveLink
                                        ) && (
                                                <CardActions>
                                                    <div className='d-flex justify-content-between'>
                                                        {event.extendedProps.devisLink && (
                                                            right_hotelier_gestion_demandes ? (
                                                                <a
                                                                    className="js-nav"
                                                                    href={event.extendedProps.devisLink}
                                                                    onClick={() => setOpen(false)}
                                                                >
                                                                    {gctrad('Devis')
                                                                    + ' ' + displayRequestRef(event.extendedProps.requestsId)}
                                                                </a>
                                                            ) : (
                                                                <span className='text-secondary'>
                                                                    {gctrad('Devis')
                                                                    + ' ' + displayRequestRef(event.extendedProps.requestsId)}
                                                                </span>
                                                            )
                                                        )}
                                                        {event.extendedProps.functionSheetLink && (
                                                            <a
                                                                className="js-nav ml-4"
                                                                href={event.extendedProps.functionSheetLink}
                                                                onClick={() => setOpen(false)}
                                                            >
                                                                {gctrad('[:myspace] Fiche de fonction')}
                                                            </a>
                                                        )}
                                                    </div>
                                                    <div className='mt-2'>
                                                        {event.extendedProps.editLink && (
                                                            <a
                                                                className="js-nav"
                                                                href={event.extendedProps.editLink}
                                                                onClick={() => setOpen(false)}
                                                            >
                                                                {gctrad('[:myspace] Modifier')}
                                                            </a>
                                                        )}
                                                    </div>
                                                    <div className='mt-2'>
                                                        {event.extendedProps.moveLink && (
                                                            <a
                                                                className="js-nav"
                                                                href={event.extendedProps.moveLink}
                                                                onClick={() => setOpen(false)}
                                                            >
                                                                {gctrad('[:myspace] Déplacer vers un autre espace')}
                                                            </a>
                                                        )}
                                                    </div>
                                                </CardActions>
                                            )}
                                    </Paper>
                                </ClickAwayListener>
                            </div>
                        </Grow>
                    )}
                </Popper>
            </ThemeProvider>
        )
    );
}

const Badge = styled.span`
    display: inline-block;

    .badge-status {
        font-size: .7em;
        line-height: 0.9em;
        font-weight: 400;
        padding: 3px 5px 1px 2px;
        margin-bottom: 1px;
        margin-left: 1rem;
        background-color: #fff;
        border: 2px solid transparent;
        border-radius: 50px;
        -webkit-border-radius: 50px;
        -moz-border-radius: 50px;
        -ms-border-radius: 50px;
        -o-border-radius: 50px;

        i {
            font-size: .8rem;
            margin-right: 5px;
        }
    }
`;

const CardContent = styled.div`
    padding: 1rem .5rem;
	&.conflit {
        background: rgba(#F0F0F0, 0.2) !important;
        border-left: 3px solid #DA1923 !important;
        border: 3px solid !important;
        border-image-slice: 1 !important;
        border-width: '3px' !important;
        border-image-source: repeating-linear-gradient(72deg, white, white 2px, #DA1923 1px, #DA1923 6px) !important;
        border-top: 0 !important;
        border-bottom: 0 !important;
        border-right: 0 !important;
    }
`;

const CardActions = styled.div`
    padding: .5rem;
    color: ${backYouTheme.palette.primary.main};
    font-size: .7em;
    font-weight: bold;
    border-top: 1px solid ${backYouTheme.palette.secondary.main};
`;

const Popper = muiStyled(MuiPopper)(({ theme }) => ({
    zIndex: 10000,
    '& > div': {
        position: 'relative',
    },
    '&[data-popper-placement*="bottom"]': {
        '& > div': {
            marginTop: '6px !important',
        },
        '& .MuiPopper-arrow': {
            top: 0,
            left: 0,
            marginTop: '-0.9em',
            width: '3em',
            height: '1em',
            '&::before': {
                borderWidth: '0 1em .95em 1em',
                borderColor: `transparent transparent ${backYouTheme.palette.primary.darkBlueLight} transparent`,
                boderBottom: 'none'
            },
        },
    },
    '&[data-popper-placement*="top"]': {
        '& > div': {
            marginBottom: '6px !important',
        },
        '& .MuiPopper-arrow': {
            bottom: 0,
            left: 0,
            marginBottom: '-0.9em',
            width: '3em',
            height: '1em',
            '&::before': {
                borderWidth: '1em 1em 0 1em',
                borderColor: `${theme.palette.background.paper} transparent transparent transparent`,
            },
        },
    },
    '&[data-popper-placement*="right"]': {
        '& > div': {
            marginLeft: '6px !important',
        },
        '& .MuiPopper-arrow': {
            left: 0,
            marginLeft: '-0.9em',
            height: '3em',
            width: '1em',
            '&::before': {
                borderWidth: '1em 1em 1em 0',
                borderColor: `transparent ${theme.palette.background.paper} transparent transparent`,
            },
        },
    },
    '&[data-popper-placement*="left"]': {
        '& > div': {
            marginRight: '6px !important',
        },
        '& .MuiPopper-arrow': {
            right: 0,
            marginRight: '-0.9em',
            height: '3em',
            width: '1em',
            '&::before': {
                borderWidth: '1em 0 1em 1em',
                borderColor: `transparent transparent transparent ${theme.palette.background.paper}`,
            },
        },
    },
}));

const Arrow = muiStyled('div')({
    position: 'absolute',
    fontSize: 7,
    width: '3em',
    height: '3em',
    '&::before': {
        content: '""',
        margin: 'auto',
        display: 'block',
        width: 0,
        height: 0,
        borderStyle: 'solid',
    },
});